import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
} from '@mui/material';
import { Add as AddIcon, Archive as ArchiveIcon, Inventory as InventoryIcon, PublishedWithChanges as PublishIcon, School as SchoolIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllCountries, getCurriculumSubjects, getCurriculums, CountryDTO, CurriculumDTO } from '../services/adminCourseService';
import { getBoards, AdminBoardDTO } from '../services/adminLocationService';
import { archiveCourse, createCourse, getMyCourses, publishCourse, TutorCourse } from '../services/tutorCourseService';

interface CurriculumSubjectOption {
  id: number;
  code: string;
  name: string;
  boardId?: number;
}

const TutorCourseManagement: React.FC = () => {
  const navigate = useNavigate();

  // Reference data
  const [countries, setCountries] = useState<CountryDTO[]>([]);
  const [allBoards, setAllBoards] = useState<AdminBoardDTO[]>([]);
  const [allSubjects, setAllSubjects] = useState<CurriculumSubjectOption[]>([]);
  const [allCurricula, setAllCurricula] = useState<CurriculumDTO[]>([]);
  const [myCourses, setMyCourses] = useState<TutorCourse[]>([]);

  // Create form visibility
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Loading state
  const [dataLoading, setDataLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState<number | null>(null);
  const [publishLoading, setPublishLoading] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Cascade selections
  const [selectedCountryId, setSelectedCountryId] = useState<number | ''>('');
  const [selectedBoardId, setSelectedBoardId] = useState<number | ''>('');
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | ''>('');
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<number | ''>('');

  // Course name
  const [courseName, setCourseName] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ─── Load reference data on mount ──────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setDataLoading(true);
      setLoadError(null);
      try {
        const [countriesData, boardsData, subjectsData, curriculaData, coursesData] = await Promise.all([
          getAllCountries().catch(() => [] as CountryDTO[]),
          getBoards().catch(() => [] as AdminBoardDTO[]),
          getCurriculumSubjects().catch(() => [] as CurriculumSubjectOption[]),
          getCurriculums().catch(() => [] as CurriculumDTO[]),
          getMyCourses().catch(() => [] as TutorCourse[]),
        ]);
        setCountries(countriesData);
        setAllBoards(boardsData);
        setAllSubjects(subjectsData);
        setAllCurricula(curriculaData);
        setMyCourses(coursesData);
      } catch (err: any) {
        setLoadError(err.message || 'Failed to load data');
      } finally {
        setDataLoading(false);
      }
    };
    init();
  }, []);

  // ─── Cascade filtering ──────────────────────────────────────────────────────
  const filteredBoards = selectedCountryId
    ? allBoards.filter((b) => b.countryId === Number(selectedCountryId))
    : [];

  const filteredSubjects = selectedBoardId
    ? allSubjects.filter((s) => s.boardId === Number(selectedBoardId))
    : [];

  const filteredCurricula =
    selectedCountryId && selectedBoardId && selectedSubjectId
      ? allCurricula.filter(
          (c) =>
            c.countryId === Number(selectedCountryId) &&
            c.boardId === Number(selectedBoardId) &&
            c.curriculumSubjectId === Number(selectedSubjectId),
        )
      : [];

  // ─── Cascade reset handlers ─────────────────────────────────────────────────
  const handleCountryChange = (value: number | '') => {
    setSelectedCountryId(value);
    setSelectedBoardId('');
    setSelectedSubjectId('');
    setSelectedCurriculumId('');
    setErrors((prev) => ({ ...prev, board: '', subject: '', curriculum: '' }));
  };

  const handleBoardChange = (value: number | '') => {
    setSelectedBoardId(value);
    setSelectedSubjectId('');
    setSelectedCurriculumId('');
    setErrors((prev) => ({ ...prev, subject: '', curriculum: '' }));
  };

  const handleSubjectChange = (value: number | '') => {
    setSelectedSubjectId(value);
    setSelectedCurriculumId('');
    setErrors((prev) => ({ ...prev, curriculum: '' }));
  };

  // ─── Form validation ────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!selectedCountryId) newErrors.country = 'Please select a country';
    if (selectedCountryId && filteredBoards.length === 0) {
      newErrors.board = 'No boards available for this country. Contact support@studystride.app to request configuration.';
    } else if (!selectedBoardId) {
      newErrors.board = 'Please select a board';
    }
    if (!selectedSubjectId) newErrors.subject = 'Please select a curriculum subject';
    if (!selectedCurriculumId) newErrors.curriculum = 'Please select a course curriculum';
    if (!courseName.trim()) newErrors.name = 'Course name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await createCourse(courseName.trim(), Number(selectedCurriculumId));
      navigate(`/tutor/courses/${created.id}/structure`);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Failed to create course';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveCourse = async (courseId: number, courseName: string) => {
    if (!window.confirm(`Archive "${courseName}"? Students will lose access, but all data is preserved and the course can be restored.`)) return;
    setArchiveLoading(courseId);
    try {
      await archiveCourse(courseId);
      setMyCourses(prev => prev.filter(c => c.id !== courseId));
    } catch {
      setSubmitError('Failed to archive course. Please try again.');
    } finally {
      setArchiveLoading(null);
    }
  };

  const handlePublishCourse = async (courseId: number, courseName: string) => {
    if (!window.confirm(`Publish "${courseName}"? This will make it active and allow students to enrol via a registration code.`)) return;
    setPublishLoading(courseId);
    try {
      const updated = await publishCourse(courseId);
      setMyCourses(prev => prev.map(c => c.id === courseId ? updated : c));
    } catch {
      setSubmitError('Failed to publish course. Please try again.');
    } finally {
      setPublishLoading(null);
    }
  };

  // ─── Status badge ────────────────────────────────────────────────────────────
  const statusColor = (status: string) => {
    if (status === 'DRAFT') return 'default';
    if (status === 'ACTIVE') return 'success';
    return 'warning';
  };

  if (dataLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">My Courses</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateForm(true)}
          >
            Create Course
          </Button>
          <Button
            variant="outlined"
            startIcon={<InventoryIcon />}
            onClick={() => navigate('/tutor/courses/archived')}
          >
            Archived
          </Button>
        </Box>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {/* ─── Create Course Form (collapsible) ──────────────────────────── */}
      <Collapse in={showCreateForm}>
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">
              <AddIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Create Course
            </Typography>
            <Button size="small" onClick={() => setShowCreateForm(false)}>Cancel</Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Country */}
          <FormControl fullWidth required error={!!errors.country}>
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountryId}
              label="Country"
              onChange={(e) => handleCountryChange(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value=""><em>Select country</em></MenuItem>
              {countries.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
            {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
          </FormControl>

          {/* Board */}
          <FormControl fullWidth required disabled={!selectedCountryId} error={!!errors.board}>
            <InputLabel>Board</InputLabel>
            <Select
              value={selectedBoardId}
              label="Board"
              onChange={(e) => handleBoardChange(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value=""><em>Select board</em></MenuItem>
              {filteredBoards.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </Select>
            {errors.board && (
              <FormHelperText>{errors.board}</FormHelperText>
            )}
            {selectedCountryId && filteredBoards.length === 0 && !errors.board && (
              <FormHelperText>
                No boards available for this country. Contact{' '}
                <a href="mailto:support@studystride.app">support@studystride.app</a>{' '}
                to request configuration.
              </FormHelperText>
            )}
          </FormControl>

          {/* Curriculum Subject */}
          <FormControl fullWidth required disabled={!selectedBoardId} error={!!errors.subject}>
            <InputLabel>Curriculum Subject</InputLabel>
            <Select
              value={selectedSubjectId}
              label="Curriculum Subject"
              onChange={(e) => handleSubjectChange(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value=""><em>Select curriculum subject</em></MenuItem>
              {filteredSubjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.code} — {s.name}</MenuItem>
              ))}
            </Select>
            {errors.subject && <FormHelperText>{errors.subject}</FormHelperText>}
          </FormControl>

          {/* Course Curriculum */}
          <FormControl
            fullWidth
            required
            disabled={!selectedCountryId || !selectedBoardId || !selectedSubjectId}
            error={!!errors.curriculum}
          >
            <InputLabel>Course Curriculum</InputLabel>
            <Select
              value={selectedCurriculumId}
              label="Course Curriculum"
              onChange={(e) => {
                setSelectedCurriculumId(e.target.value === '' ? '' : Number(e.target.value));
                setErrors((prev) => ({ ...prev, curriculum: '' }));
              }}
            >
              <MenuItem value=""><em>Select course curriculum</em></MenuItem>
              {filteredCurricula.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.version}{c.level ? ` — ${c.level}` : ''}
                </MenuItem>
              ))}
            </Select>
            {errors.curriculum && <FormHelperText>{errors.curriculum}</FormHelperText>}
          </FormControl>

          {/* Course Name */}
          <TextField
            fullWidth
            required
            label="Course Name"
            value={courseName}
            onChange={(e) => {
              setCourseName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
            }}
            inputProps={{ maxLength: 255 }}
            error={!!errors.name}
            helperText={errors.name || ''}
            placeholder="e.g. National 5 Chemistry Class"
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : <AddIcon />}
            >
              {submitting ? 'Creating…' : 'Create Course'}
            </Button>
            <Button size="large" onClick={() => setShowCreateForm(false)}>Cancel</Button>
          </Box>
        </Box>
      </Paper>
      </Collapse>

      {/* ─── Empty state ────────────────────────────────────────────────── */}
      {myCourses.length === 0 && !showCreateForm && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No courses yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create your first course to get started with your students.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setShowCreateForm(true)}>
            Create your first course
          </Button>
        </Paper>
      )}

      {/* ─── Existing Courses ───────────────────────────────────────────── */}
      {myCourses.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Your Courses</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Board</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myCourses.map((course) => (
                  <TableRow key={course.id} hover>
                    <TableCell
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/tutor/courses/${course.id}`)}
                    >
                      {course.name}
                    </TableCell>
                    <TableCell>{course.board || '—'}</TableCell>
                    <TableCell>{course.subjectName || '—'}</TableCell>
                    <TableCell>
                      <Chip label={course.status} color={statusColor(course.status) as any} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      {course.status === 'DRAFT' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={publishLoading === course.id ? <CircularProgress size={14} /> : <PublishIcon />}
                          disabled={publishLoading === course.id}
                          onClick={() => handlePublishCourse(course.id, course.name)}
                          sx={{ mr: 1 }}
                        >
                          Publish
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/tutor/courses/${course.id}`)}
                        sx={{ mr: 1 }}
                      >
                        Structure
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate(`/tutor/courses/${course.id}/roster`)}
                        sx={{ mr: 1 }}
                      >
                        Roster
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        onClick={() => navigate(`/tutor/courses/${course.id}/tests`)}
                        sx={{ mr: 1 }}
                      >
                        Tests
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={archiveLoading === course.id ? <CircularProgress size={14} /> : <ArchiveIcon />}
                        disabled={archiveLoading === course.id}
                        onClick={() => handleArchiveCourse(course.id, course.name)}
                      >
                        Archive
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default TutorCourseManagement;
